/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 1st 2022, 9:42:18 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";

export interface FixedListAutoCompleteProps<TOption, Multiple extends boolean>
  extends Pick<AutocompleteProps<TOption, Multiple, boolean, false>, "options">,
    Partial<Omit<AutocompleteProps<TOption, false, boolean, false>, "options" | "onChange">> {
  textFieldProps?: TextFieldProps; // Allowing one to pass down any props to the text field that is rendered by the autocomplete widget

  onChangeCallBack: (selectedOption: TOption) => void;
}

export const FixedListAutoComplete = <TOption, Multiple extends boolean = false>({
  id,
  options,
  textFieldProps,
  value,
  onChangeCallBack,
  renderOption,
  ...otherProps
}: FixedListAutoCompleteProps<TOption, Multiple>) => {
  const handleChange = (
    event: React.SyntheticEvent,
    selectedOption: NonNullable<TOption>,
    reason: any,
    details: any
  ): void => {
    onChangeCallBack?.(selectedOption);
  };

  return (
    <Autocomplete
      disablePortal
      id={id}
      options={options}
      renderInput={(params) => <TextField {...params} label={textFieldProps?.label} {...textFieldProps} />}
      renderOption={renderOption}
      value={value}
      onChange={handleChange}
      {...otherProps}
    />
  );
};
